import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { sameValues, useUnsavedGuard } from '../ui/unsaved';
import { actionLabel } from './labels';
import { scopeLabel } from './permissionUi';
import type { AdminUser, CatalogueEntry, OverrideEntry } from './types';
import { Notice } from '../ui/Notice';

type OverrideData = { userId: string; overrides: OverrideEntry[]; catalogue: CatalogueEntry[] };
/** `default` = no override (whatever the roles give), `deny`, or an allowed scope. */
type Choice = 'default' | 'deny' | 'all' | 'own';
const key = (resource: string, action: string) => `${resource}\u0000${action}`;

function toChoices(data: OverrideData): Record<string, Choice> {
  const out: Record<string, Choice> = {};
  for (const o of data.overrides) out[key(o.resource, o.action)] = o.allow ? o.scope : 'deny';
  return out;
}

function Editor({ userId, data }: { userId: string; data: OverrideData }) {
  const queryClient = useQueryClient();
  const [choices, setChoices] = useState(() => toChoices(data));
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () => {
      const overrides: OverrideEntry[] = [];
      for (const entry of data.catalogue)
        for (const action of entry.actions) {
          const choice = choices[key(entry.resource, action)] ?? 'default';
          if (choice === 'default') continue;
          overrides.push(
            choice === 'deny'
              ? { resource: entry.resource, action, scope: 'all', allow: false }
              : { resource: entry.resource, action, scope: choice, allow: true },
          );
        }
      return api<OverrideData>(`/api/admin/users/${userId}/overrides`, { method: 'PUT', body: { overrides } });
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(['admin', 'overrides', userId], fresh);
      setSaved(true);
    },
  });

  // owner 2026-10-23 21:14: leaving with unsaved exceptions asks first ("default" = no exception)
  const set = (c: Record<string, Choice>) => Object.fromEntries(Object.entries(c).filter(([, v]) => v !== 'default'));
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(set(choices), set(toChoices(data))),
    save: async () => {
      await save.mutateAsync();
      return true;
    },
  });

  return (
    <form
      ref={guardRef}
      className="matrix"
      data-feature-id="identity.roles.permissions"
      onSubmit={(e) => {
        e.preventDefault();
        setSaved(false);
        save.mutate();
      }}
    >
      {save.error && (
        <p className="alert" role="alert">
          {save.error instanceof ApiError ? save.error.message : 'Nie udało się zapisać wyjątków'}
        </p>
      )}
      {saved && (
        <Notice onClose={() => setSaved(false)}>
          Zapisano.
        </Notice>
      )}
      {data.catalogue.map((entry) => (
        <fieldset key={entry.resource} className="panel resource">
          <legend>{entry.label}</legend>
          <ul className="perm-list">
            {entry.actions.map((action) => {
              const id = `ov-${entry.resource}-${action}`;
              return (
                <li key={action}>
                  <label htmlFor={id}>{actionLabel(entry, action)}</label>
                  <select
                    id={id}
                    value={choices[key(entry.resource, action)] ?? 'default'}
                    onChange={(e) => setChoices({ ...choices, [key(entry.resource, action)]: e.target.value as Choice })}
                  >
                    <option value="default">Jak w rolach</option>
                    {entry.scopes.map((scope) => (
                      <option key={scope} value={scope}>
                        Zezwól: {scopeLabel[scope].toLowerCase()}
                      </option>
                    ))}
                    <option value="deny">Zabroń</option>
                  </select>
                </li>
              );
            })}
          </ul>
        </fieldset>
      ))}
      <div className="sticky-actions">
        <button className="btn primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : 'Zapisz wyjątki'}
        </button>
      </div>
    </form>
  );
}

export function UserOverridesPage() {
  const { id = '' } = useParams();
  const user = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api<{ users: AdminUser[] }>('/api/admin/users').then((r) => r.users),
  });
  const data = useQuery({ queryKey: ['admin', 'overrides', id], queryFn: () => api<OverrideData>(`/api/admin/users/${id}/overrides`) });
  const name = user.data?.find((u) => u.id === id)?.name;

  return (
    <section className="page" data-feature-id="identity.roles.permissions">
      <p className="crumb">
        <Link to="/uzytkownicy">Użytkownicy</Link>
      </p>
      <div className="page-head">
        <h1>Wyjątki: {name ?? '…'}</h1>
      </div>
      <p className="muted lede">
        Uprawnienia wynikają z ról tej osoby. Tutaj możesz coś dodatkowo zezwolić albo zabronić tylko jej. „Zabroń” wygrywa z rolą.
      </p>
      {data.error && (
        <p className="alert" role="alert">
          {data.error instanceof ApiError ? data.error.message : 'Nie udało się wczytać uprawnień'}
        </p>
      )}
      {data.data && <Editor key={id} userId={id} data={data.data} />}
    </section>
  );
}
